import React, { Suspense } from 'react';
import { useLazyComponent } from '@trello/use-lazy-component';
import { PermissionsContextProvider } from 'app/src/components/BoardDashboardView/PermissionsContext';
import { SingleBoardViewProvider } from 'app/src/components/BoardViewContext/SingleBoardViewProvider';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { SingleBoardViewFiltersProvider } from 'app/src/components/ViewFilters/SingleBoardViewFiltersProvider';
import { useFixForBackboneNavigationBug } from 'app/src/components/ViewFilters/useUrlParams';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from 'app/src/components/WorkspaceNavigation';

import styles from './SingleBoardTableView.less';
import classNames from 'classnames';

interface SingleBoardTableViewProps {
  idBoard: string;
  navigateToCard: (id: string) => void;
  closeView: () => void;
}

export const SingleBoardTableView: React.FC<SingleBoardTableViewProps> = ({
  idBoard,
  navigateToCard,
  closeView,
}: SingleBoardTableViewProps) => {
  const BoardTableView = useLazyComponent(
    () => import(/* webpackChunkName: "table-view" */ './BoardTableView'),
    { namedImport: 'BoardTableView' },
  );

  const [{ expanded: workspaceNavigationExpanded }] = useWorkspaceNavigation();

  const [
    { hidden: workspaceNavigationHidden },
  ] = useWorkspaceNavigationHidden();

  // To repro the bug this fixes: Load a single board table view. Make sure
  // no filters are set, then sort by due date. Refresh the page. After it
  // loads, try to disable the due date sorting. Without this fix, nothing
  // will happen. With this fix, you can disable the sort.
  useFixForBackboneNavigationBug();

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-panorama',
        feature: 'Table View',
      }}
    >
      <Suspense fallback={null}>
        <PermissionsContextProvider idBoard={idBoard}>
          <SingleBoardViewFiltersProvider idBoard={idBoard}>
            <SingleBoardViewProvider
              idBoard={idBoard}
              navigateToCard={navigateToCard}
              closeView={closeView}
            >
              <div
                className={classNames(styles.sbtvWrapper, {
                  [styles.collapsedWorkspaceNavigation]:
                    !workspaceNavigationHidden && !workspaceNavigationExpanded,
                })}
              >
                <BoardTableView />
              </div>
            </SingleBoardViewProvider>
          </SingleBoardViewFiltersProvider>
        </PermissionsContextProvider>
      </Suspense>
    </ErrorBoundary>
  );
};
